class AudioPlayer extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.audio = null;
    this.isPlaying = false;
    this.isDragging = false;
  }

  connectedCallback() {
    this.#render();
    this.#setupEventListeners();
  }

  #render() {
    const src = this.getAttribute('src');
    const label = this.getAttribute('data-label');

    if (!src) {
      console.warn('AudioPlayer: src attribute is required');
      return;
    }

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
        }

        * {
          box-sizing: border-box;
        }
        
        .audio-player {
          width: 100%;

          display: flex;
          flex-flow: row nowrap;
          align-items: center;
          
          border: 1px solid oklch(var(--color-on-surface) / 1);
          
          overflow: clip;
        }
          
        .audio-player__progress-bar-container {
          position: relative;

          flex: 1;
          padding-left: 2px;
          display: flex;
          align-items: center;

          background: oklch(var(--color-on-surface) / 1);
          isolation: isolate;
        }

        .audio-player__label {
          display: inline-flex;
          font: var(--text-grotesk-p--small);
          color: oklch(var(--color-on-surface-inverse) / 1);
          mix-blend-mode: difference;
          pointer-events: none;

          text-box: trim-both cap alphabetic;
        }

        .audio-player__play-pause {
          height: 21x;
          width: 24px;

          background: oklch(var(--color-surface) / 1);
          border: none;
          color: oklch(var(--color-on-surface) / 1);
          
          display: flex;
          align-items: center;
          justify-content: center;

          cursor: pointer;
          mix-blend-mode: difference;
        }

        .audio-player__play-icon,
        .audio-player__pause-icon {
          width: auto;
          height: 12px;
        }

        .audio-player__play-icon.hidden,
        .audio-player__pause-icon.hidden {
          display: none;
        }

        .audio-player__progress-bar {
          position: absolute;
          inset: 0px auto 0px 0px;
          width: 0%;
          background: oklch(var(--color-surface) / 1);
          transition: width 0.1s ease-out;
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .audio-player__play-pause,
          .audio-player__progress-bar {
            transition: none;
          }
        }

        @media (max-width: 768px) {
          .audio-player__label {
            font: var(--text-grotesk-menu--small);
          }

          .audio-player__play-icon,
          .audio-player__pause-icon {
            height: 8px;
          }
        }
      </style>
      
      <div class="audio-player">
        <div class="audio-player__progress-bar-container">
          <div class="audio-player__progress-bar"></div>

          <span class="audio-player__label">[ ${
            label || 'Voice / Play'
          } ]</span>
        </div>

        <button class="audio-player__play-pause" type="button" aria-label="Play">
          <svg class="audio-player__play-icon" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 9"><path d="M6 4.667.75 8.708V.625L6 4.667Z" fill="currentColor"/></svg>
          <svg class="audio-player__pause-icon hidden" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 5 8"><path fill="currentColor" d="M0 0h2v8H0zM3 0h2v8H3z"/></svg>
        </button>
      
        <audio src="${src}" type="audio/mpeg">
        </audio>
      </div>
    `;

    this.audio = this.shadowRoot.querySelector('audio');
    this.player = this.shadowRoot.querySelector('.audio-player');
    this.playPauseButton = this.shadowRoot.querySelector(
      '.audio-player__play-pause'
    );
    this.playIcon = this.shadowRoot.querySelector('.audio-player__play-icon');
    this.pauseIcon = this.shadowRoot.querySelector('.audio-player__pause-icon');

    this.progressBar = this.shadowRoot.querySelector(
      '.audio-player__progress-bar'
    );
  }

  #setupEventListeners() {
    if (!this.audio) return;

    // Play/Pause button
    this.playPauseButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#togglePlayPause();
    });

    // Audio events
    this.audio.addEventListener('timeupdate', () => this.#updateProgress());
    this.audio.addEventListener('ended', () => this.#handleAudioEnd());

    // Progress bar interactions
    this.player.addEventListener('click', (e) => this.#handleProgressClick(e));
    this.player.addEventListener('mousedown', (e) =>
      this.#handleProgressDragStart(e)
    );

    // Global mouse events for dragging
    document.addEventListener('mousemove', (e) => this.#handleProgressDrag(e));
    document.addEventListener('mouseup', () => this.#handleProgressDragEnd());

    // Touch events for mobile
    this.progressBar.addEventListener('touchstart', (e) =>
      this.#handleProgressDragStart(e)
    );
    document.addEventListener('touchmove', (e) => this.#handleProgressDrag(e));
    document.addEventListener('touchend', () => this.#handleProgressDragEnd());
  }

  #togglePlayPause() {
    if (!this.audio) return;

    if (this.isPlaying) {
      this.audio.pause();
    } else {
      this.audio.play();
    }

    this.isPlaying = !this.isPlaying;
    this.#updatePlayPauseButton();
  }

  #updatePlayPauseButton() {
    if (this.isPlaying) {
      this.playIcon.classList.add('hidden');
      this.pauseIcon.classList.remove('hidden');
      this.playPauseButton.setAttribute('aria-label', 'Pause');
    } else {
      this.playIcon.classList.remove('hidden');
      this.pauseIcon.classList.add('hidden');
      this.playPauseButton.setAttribute('aria-label', 'Play');
    }
  }

  #updateProgress() {
    if (!this.audio || this.isDragging) return;

    const currentTime = this.audio.currentTime;
    const duration = this.audio.duration;

    if (duration > 0) {
      const progress = (currentTime / duration) * 100;
      this.progressBar.style.width = `${progress}%`;
    }
  }

  #handleAudioEnd() {
    this.isPlaying = false;
    this.#updatePlayPauseButton();
    this.progressBar.style.width = '0%';
  }

  #handleProgressClick(e) {
    if (!this.audio || this.isDragging) return;

    // Don't handle progress clicks on the play/pause button
    if (e.target.closest('.audio-player__play-pause')) {
      return;
    }

    const rect = this.player.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progress = clickX / rect.width;
    const newTime = progress * this.audio.duration;

    this.audio.currentTime = Math.max(
      0,
      Math.min(newTime, this.audio.duration)
    );
  }

  #handleProgressDragStart(e) {
    // Don't start dragging if clicking on the play/pause button
    if (e.target.closest('.audio-player__play-pause')) {
      return;
    }

    this.isDragging = true;
    e.preventDefault();
  }

  #handleProgressDrag(e) {
    if (!this.isDragging || !this.audio) return;

    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    const rect = this.player.getBoundingClientRect();
    const dragX = clientX - rect.left;
    const progress = Math.max(0, Math.min(dragX / rect.width, 1));

    this.progressBar.style.width = `${progress * 100}%`;
  }

  #handleProgressDragEnd() {
    if (!this.isDragging || !this.audio) return;

    const widthValue = this.progressBar.style.width || '0%';
    const progress = parseFloat(widthValue) / 100;
    const newTime = progress * this.audio.duration;

    this.audio.currentTime = Math.max(
      0,
      Math.min(newTime, this.audio.duration)
    );
    this.isDragging = false;
  }
}

customElements.define('audio-player', AudioPlayer);
